import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Manufacturer } from "../../../../core/models/entities/Manufacturer";
import { Organization } from "../../../../core/models/entities/Organization";
import { LogoOptions } from "../../../../core/models/entities/LogoOptions";
import {
  Commission,
  CommissionProfile,
} from "../../../../core/models/entities/Commission";
import { Role } from "../../../../core/models/entities/Role";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { useErrorMessage } from "../../../hooks/useErrorMessage";
import { useCustomerActionHandler } from "../hooks/useCustomerActionHandler";
import { isCustomerFormInvalid } from "../functions/is-customer-form-valid.function";
import { CustomerForm } from "../components/customer.form";
import {
  CREATE_COMPANY_FIELD_ORDER,
  FieldErrorMap,
  RequiredFieldError,
  buildMissingFieldsMessage,
  getCompanyFieldErrors,
  getShippingFieldErrors,
  scrollToFirstErrorField,
  toFieldErrorMap,
} from "../../customer-update/models/required-fields";

export const WIZARD_STEPS = [
  { ordinal: 1, label: "Company" },
  { ordinal: 2, label: "Contact & first user" },
  { ordinal: 3, label: "Pricing" },
  { ordinal: 4, label: "Brands" },
  { ordinal: 5, label: "Review & create" },
] as const;

export interface CustomerCreateWizard_VM {
  form: CustomerForm;
  setForm: (form: CustomerForm) => void;
  step: number;
  goToStep: (step: number) => void;
  goNext: () => void;
  goBack: () => void;
  /** Highest step the user has reached (enables nav clicks back and forth). */
  maxVisitedStep: number;
  isStepValid: (step: number) => boolean;
  isFormValid: boolean;
  /** Required-field errors for the Company step, by field name. */
  fieldErrors: FieldErrorMap;
  /** True after Next was blocked on the Company step — show every error. */
  showFieldErrors: boolean;
  /** "Please fill in: ..." line for the footer after a blocked Next. */
  validationSummary?: string;
  isLoading: boolean;
  onCreate: () => Promise<void>;
  onCancel: () => void;
  shippingDiffers: boolean;
  setShippingDiffers: (differs: boolean) => void;
  showVendorPicker: boolean;
  vendors?: Organization[];
  selectedVendor?: Organization;
  // Pricing (lazy: mounts when the pricing step is first visited)
  logoOptions?: LogoOptions;
  updateCommission: (commission: CommissionProfile) => void;
  commissionProfile: CommissionProfile;
  isCommissionCustomized: boolean;
  vendorDefaultCommission?: CommissionProfile;
  // Brands (lazy: mounts when the brands step is first visited)
  manufacturers?: Manufacturer[];
  isManufacturersLoading: boolean;
}

export function useCustomerCreateFormViewModel(
  vendors: Organization[] | undefined
): CustomerCreateWizard_VM {
  const { t } = useTranslation();
  const { organizationRepository, logoRepository, manufacturerRepository } =
    useRepositoriesContext();
  const { profile, role } = useProfileContext();
  const { data: vendor } = organizationRepository.useVendor(
    profile?.userOrganizationInformations?.organizationId
  );

  // ----- Wizard step state -----
  const [step, setStep] = useState(1);
  const [maxVisitedStep, setMaxVisitedStep] = useState(1);

  function goToStep(next: number) {
    if (next < 1 || next > WIZARD_STEPS.length) return;
    setStep(next);
    setMaxVisitedStep((prev) => Math.max(prev, next));
  }

  // ----- Lazy data (per-step mount) -----
  const pricingVisited = maxVisitedStep >= 3;
  const brandsVisited = maxVisitedStep >= 4;
  const { data: logoOptions } = logoRepository.useOptions({
    enabled: pricingVisited,
  });
  const { data: manufacturers, isLoading: isManufacturersLoading } =
    manufacturerRepository.useManufacturers({ enabled: brandsVisited });

  // ----- Form state -----
  const [form, setForm] = useState<CustomerForm>({
    name: "",
    contactEmail: "",
    email: "",
    contactPerson: "",
    contactPhone: "",
    phone: "",
    address: "",
    postCode: "",
    city: "",
    country: "",
    cvr: "",
    excludedManufacturers: [] as Manufacturer[],
    parentId: undefined,
    eori: "",
    shippingName: "",
    shippingAddress: "",
    shippingPostCode: "",
    shippingCity: "",
    shippingCountry: "",
    shippingContactPerson: "",
    shippingContactPhone: "",
    shippingContactEmail: "",
    vatRate: vendor?.defaultVatRate,
    hidePrices: false,
  } as CustomerForm);
  const [shippingDiffers, setShippingDiffers] = useState(false);
  const [commissionProfile, setCommissionProfile] =
    useState<CommissionProfile>();
  const [vendorCommissionProfile, setVendorCommissionProfile] =
    useState<CommissionProfile>();
  const [isCommissionCustomized, setIsCommissionCustomized] = useState(false);
  const { handleGoToCustomerList, handleCreate, isLoading } =
    useCustomerActionHandler();
  const [commissionLoading, setCommissionLoading] = useState(false);
  const { tError } = useErrorMessage();

  useEffect(() => {
    if (vendor) {
      setForm((prev) => ({
        ...prev,
        vatRate: prev.vatRate ?? vendor.defaultVatRate,
      }));
      // Set vendor's commission as default for new customers
      if (vendor.commission && !commissionProfile) {
        setVendorCommissionProfile(vendor.commission);
        setCommissionProfile(vendor.commission);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendor]);

  const previousCountryRef = useRef<string | undefined>();
  useEffect(() => {
    if (previousCountryRef.current !== form.country && form.country === "DK") {
      setForm((prev) => ({ ...prev, vatRate: 25 }));
    }
    previousCountryRef.current = form.country;
  }, [form.country]);

  // ----- Validation -----
  // Every company/address field is mandatory; the shipping fields too while
  // "shipping address differs" is checked.
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const requiredFieldErrors: RequiredFieldError[] = [
    ...getCompanyFieldErrors(form, CREATE_COMPANY_FIELD_ORDER),
    ...getShippingFieldErrors(form, shippingDiffers),
  ];

  function isStepValid(s: number): boolean {
    switch (s) {
      case 1:
        return (
          requiredFieldErrors.length === 0 &&
          (role !== Role.Admin || !!form.parentId)
        );
      case 2:
        return !!form.contactPerson && !!form.contactEmail && !!form.contactPhone;
      default:
        return true;
    }
  }

  function goNext() {
    if (step === 1 && requiredFieldErrors.length > 0) {
      setShowFieldErrors(true);
      setTimeout(() => scrollToFirstErrorField(requiredFieldErrors), 100);
      return;
    }
    if (!isStepValid(step)) {
      toast.error(t("customerCreate.fillRequiredFields"));
      return;
    }
    goToStep(step + 1);
  }

  function goBack() {
    if (step > 1) setStep(step - 1);
  }

  // ----- Create (same submission logic/endpoints as before) -----
  function handleCreateClick(): Promise<void> {
    const formValid =
      !isCustomerFormInvalid(form) && requiredFieldErrors.length === 0;
    if (!formValid) {
      setShowFieldErrors(true);
      toast.error(t("customerCreate.formInvalid"));
      return Promise.reject();
    }

    if (formValid && !form.parentId && role === Role.Admin) {
      toast.error(t("customerCreate.adminsMustChooseVendor"));
      return Promise.reject();
    }

    if (formValid && form.parentId && role !== Role.Admin) {
      toast.error(t("customerCreate.illegalAction"));
      return Promise.reject();
    }

    // When shipping does not differ, the shipping fields go out empty —
    //  identical to the previous create screen's default.
    const shippingFields = shippingDiffers
      ? {}
      : {
          shippingName: "",
          shippingAddress: "",
          shippingPostCode: "",
          shippingCity: "",
          shippingCountry: "",
          shippingContactPerson: "",
          shippingContactPhone: "",
          shippingContactEmail: "",
        };

    const s = {
      ...form,
      ...shippingFields,
      isVendor: false,
      parentId:
        form.parentId ?? profile!.userOrganizationInformations.organizationId,
      id: 0,
      excludedManufacturers: form.excludedManufacturers?.map((x) => x.id),
      commission: undefined,
    } as Organization;

    async function transaction() {
      const org = await handleCreate(s);
      if (commissionProfile) {
        setCommissionLoading(true);
        const result = await organizationRepository.setCommissionProfile(
          org.id,
          commissionProfile
        );
        setCommissionLoading(false);
        return result;
      }
    }

    return transaction()
      .then(() => {
        toast.success(t("customerCreate.customerCreated"));
        handleGoToCustomerList();
      })
      .catch((e) => Promise.reject(tError(e)));
  }

  function updateCommission(commission: CommissionProfile): void {
    setCommissionProfile(commission);
    setIsCommissionCustomized(true);
  }

  function handleCancel() {
    handleGoToCustomerList();
  }

  return {
    form,
    setForm,
    step,
    goToStep,
    goNext,
    goBack,
    maxVisitedStep,
    isStepValid,
    isFormValid:
      !isCustomerFormInvalid(form) && requiredFieldErrors.length === 0,
    fieldErrors: toFieldErrorMap(requiredFieldErrors),
    showFieldErrors,
    validationSummary: showFieldErrors
      ? buildMissingFieldsMessage(requiredFieldErrors)
      : undefined,
    isLoading: isLoading || commissionLoading,
    onCreate: handleCreateClick,
    onCancel: handleCancel,
    shippingDiffers,
    setShippingDiffers,
    showVendorPicker: profile?.userOrganizationInformations.role === Role.Admin,
    vendors,
    selectedVendor: vendors
      ? vendors.find((x) => x.id === form.parentId)
      : undefined,
    logoOptions,
    updateCommission,
    commissionProfile:
      commissionProfile ??
      vendorCommissionProfile ??
      ({
        typeOnly: [] as Commission[],
        methods: [] as Commission[],
      } as CommissionProfile),
    isCommissionCustomized,
    vendorDefaultCommission: vendorCommissionProfile,
    manufacturers,
    isManufacturersLoading: brandsVisited && isManufacturersLoading,
  };
}
